import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTitle,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useTranslation } from 'react-i18next';
import { useApiQuery, useApiMutation } from '@/hooks/useApi';
import { useProfile } from '@/hooks/useProfile';
import {
  Announcement,
  AnnouncementsApiResponse,
  MarkAsViewedPayload,
  MarkAsViewedResponse,
} from '@/interface/announcement';
import { getLocalizedContent } from '@/util/announcementUtil';
import { useLanguage } from '@/hooks/useLanguage';

type AnnouncementType = 'error' | 'warning' | 'info';
const ANNOUNCEMENT_DISMISSED_KEY = 'dismissedAnnouncementIds';

const getDismissedFromStorage = (): string[] => {
  const raw = localStorage.getItem(ANNOUNCEMENT_DISMISSED_KEY);
  return raw ? JSON.parse(raw) : [];
};

const addDismissedToStorage = (id: string): void => {
  const dismissed = getDismissedFromStorage();
  if (!dismissed.includes(id)) {
    const updated = [...dismissed, id];
    localStorage.setItem(ANNOUNCEMENT_DISMISSED_KEY, JSON.stringify(updated));
  }
};

const isAnnouncementInDateRange = (announcement: Announcement): boolean => {
  const currentDate = new Date();
  const hasStartDate =
    announcement.startDate && announcement.startDate.trim() !== '';
  const hasEndDate = announcement.endDate && announcement.endDate.trim() !== '';

  // If no date constraints, show the announcement
  if (!hasStartDate && !hasEndDate) {
    return true;
  }

  // Check start date
  if (hasStartDate && announcement.startDate) {
    const startDate = new Date(announcement.startDate);
    if (currentDate < startDate) {
      return false;
    }
  }

  // Check end date
  if (hasEndDate && announcement.endDate) {
    const endDate = new Date(announcement.endDate);
    endDate.setHours(23, 59, 59, 999);
    if (currentDate > endDate) {
      return false;
    }
  }

  return true;
};
const iconMap: Record<AnnouncementType, React.ReactNode> = {
  error: <ErrorOutlineIcon />,
  warning: <WarningAmberOutlinedIcon />,
  info: <InfoOutlinedIcon />,
};

const bgColorMap: Record<AnnouncementType, string> = {
  error: '#fef2f2',
  warning: '#fff7ed',
  info: '#eff6ff',
};

const borderColorMap: Record<AnnouncementType, string> = {
  error: '#ffc5c5',
  warning: '#fed7aa',
  info: '#bfdbfe',
};

const textColorMap: Record<AnnouncementType, string> = {
  error: '#951112',
  warning: '#993210',
  info: '#1d3faf',
};

export default function AnnouncementBanners() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation(['announcement']);
  const [expandedMessages, setExpandedMessages] = useState<
    Record<string, boolean>
  >({});

  const { profile } = useProfile();
  const currentLanguage = useLanguage();
  const userId = profile?.value?.[0]?.eXpMember?.Uuid;

  const { data: announcementsResponse } = useApiQuery<AnnouncementsApiResponse>(
    ['/profile/announcements', userId || ''],
    '/profile/announcements',
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
      gcTime: 10 * 60 * 1000,
      staleTime: 10 * 60 * 1000,
    },
    false,
    {
      userId: userId || '',
    },
  );

  const sortedAnnouncements = useMemo(() => {
    // console.log('announcements', announcements);
    if (!announcements) return [];

    return [...announcements].sort(
      (a, b) =>
        new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
    );
  }, [announcements]);
  // console.log('sortedAnnouncements', sortedAnnouncements);

  const { mutateAsync: markAsViewed } = useApiMutation<
    MarkAsViewedResponse,
    MarkAsViewedPayload
  >({
    url: '/profile/announcements',
    method: 'put',
    options: { retry: 2 },
  });

  // Update announcements when API response changes
  useEffect(() => {
    if (announcementsResponse?.success && announcementsResponse?.data) {
      const dismissed = getDismissedFromStorage();

      const filtered = announcementsResponse.data.filter((a) => {
        // Filter out dismissed announcements
        if (dismissed.includes(a.id)) {
          return false;
        }

        // Check if announcement is within date range
        return isAnnouncementInDateRange(a);
      });

      setAnnouncements(filtered);
    }
  }, [announcementsResponse]);

  // @ts-ignore only for dev and test environments
  // window.__ADD_BANNER__ = (type: AnnouncementType) => {
  //   const id = `${type}-${Date.now()}`;
  //   const newBanner: Announcement = {
  //     id,
  //     type,
  //     title: {
  //       error: 'Test Error',
  //       warning: 'Test Warning',
  //       info: 'Test Info',
  //     }[type],
  //     message: {
  //       error: 'This is a simulated error banner.',
  //       warning: 'This is a simulated warning banner.',
  //       info: 'This is a simulated info banner.',
  //     }[type],
  //     version: 1,
  //     updatedOn: new Date().toISOString(),
  //     dType: 'Announcement',
  //     customFields: [],
  //     status: 'active',
  //     runIndefinitely: true,
  //     uId: id,
  //     createdBy: 'test@example.com',
  //     expireAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  //     active: true,
  //     archived: false,
  //     createdOn: new Date().toISOString(),
  //     displayType: 'show_every_login',
  //   };
  //   setAnnouncements((prev) => [newBanner, ...prev]);
  // };

  const handleDismiss = async (id: string) => {
    const announcement = announcements.find((a) => a.id === id);
    addDismissedToStorage(id);

    if (!announcement || !userId) {
      // If no announcement found or no userId, just remove from local state
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
      return;
    }

    // Check if the announcement should be marked as viewed
    if (announcement.displayType === 'show_once_per_user') {
      try {
        await markAsViewed({
          userId,
          announcementId: id,
        });
        // console.log('Announcement marked as viewed successfully');
      } catch (error) {
        console.error('Error marking announcement as viewed:', error);
        // Even if the API call fails, we still want to remove the banner from UI
      }
    }

    // Remove the announcement from local state regardless of displayType
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
  };

  const toggleMessage = (id: string) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const remaining = announcements.length - 3;

  return (
    <Box
      data-testid="announcement-banners-container"
      sx={{ px: { xs: 0, sm: 3 }, py: 1 }}
    >
      {(showAll ? sortedAnnouncements : sortedAnnouncements.slice(0, 3)).map(
        (a) => (
          <Box
            key={a.id}
            data-testid={`announcement-banner-${a.id}`}
            sx={{
              width: '100%',
              border: `1px solid ${borderColorMap[a.type]}`,
              backgroundColor: bgColorMap[a.type],
              color: textColorMap[a.type],
              borderRadius: 2,
              borderLeft: `4px solid ${borderColorMap[a.type]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              overflow: 'auto',
              px: 2,
              py: 1.5,
              mb: 1,
            }}
            role="alert"
          >
            <Box
              data-testid={`announcement-content-${a.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box data-testid={`announcement-icon-${a.id}`}>
                {iconMap[a.type]}
              </Box>
              <Box
                data-testid={`announcement-text-${a.id}`}
                sx={{
                  fontSize: 14,
                  lineHeight: 1.2,
                  textAlign: 'left',
                }}
              >
                <AlertTitle
                  data-testid={`announcement-title-${a.id}`}
                  sx={{ fontSize: { xs: '12px' }, fontWeight: 'bold' }}
                >
                  {getLocalizedContent(a.title, currentLanguage, a.title)}
                </AlertTitle>
                <TruncatedText
                  text={getLocalizedContent(
                    a.message,
                    currentLanguage,
                    a.message,
                  )}
                  id={a.id}
                  isExpanded={!!expandedMessages[a.id]}
                  toggle={toggleMessage}
                  color={textColorMap[a.type]}
                />
              </Box>
            </Box>
            <IconButton
              data-testid={`announcement-dismiss-${a.id}`}
              onClick={() => handleDismiss(a.id)}
              sx={{ color: textColorMap[a.type] }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      )}

      {/* Toggle Button */}
      {announcements.length > 3 && (
        <Box
          data-testid="announcement-toggle-container"
          sx={{ textAlign: 'center' }}
        >
          <Box
            component="button"
            data-testid="announcement-toggle-button"
            onClick={() => setShowAll((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              backgroundColor: 'white',
              px: 2,
              py: 1,
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '12px', md: '14px' },
              color: '#374151',
              cursor: 'pointer',
              margin: 'auto',
              transition: 'background 0.2s',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }}
          >
            {showAll
              ? t('showLess')
              : t(remaining > 1 ? 'showMore_plural' : 'showMore', {
                  count: remaining,
                })}
            <ExpandMoreRoundedIcon
              data-testid="announcement-toggle-icon"
              sx={{
                transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                fontSize: 20,
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

const TruncatedText = ({
  text,
  id,
  isExpanded,
  toggle,
  color,
}: {
  text: string;
  id: string;
  isExpanded: boolean;
  toggle: (id: string) => void;
  color: string;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['announcement']);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const truncateLength = isSmallScreen ? 70 : 200;

  return (
    <Box data-testid={`announcement-message-${id}`} component="span">
      {text.length > truncateLength && !isExpanded
        ? `${text.slice(0, truncateLength)}... `
        : text + ' '}
      {text.length > truncateLength && (
        <Box
          component="button"
          data-testid={`announcement-read-more-${id}`}
          onClick={() => toggle(id)}
          sx={{
            color,
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: '12px',
            cursor: 'pointer',
            ml: 1,
          }}
        >
          {isExpanded ? t('showLess') : t('readMore')}
        </Box>
      )}
    </Box>
  );
};
